/*
 * Primitive shapes emitted by the composite layout engines (org chart, timeline,
 * process flow) and consumed by taskpane.ts, which maps them onto native
 * PowerPoint shapes (addGeometricShape / addLine / addTextBox) and groups them.
 * All geometry is in POINTS (a 16:9 slide is 960x540pt).
 */

export interface PrimStyle {
  fill?: string; // hex without '#'
  lineColor?: string;
  lineWeight?: number; // points
  fontSize?: number;
  fontColor?: string;
  bold?: boolean;
  align?: "left" | "center" | "right";
}

export interface BoxPrim {
  // Entity/structure shapes add parallelogram (partnership), hexagon (trust),
  // diamond, and triangle (flow arrowheads) to the base set.
  kind: "rect" | "roundRect" | "ellipse" | "chevron" | "parallelogram" | "hexagon" | "diamond" | "triangle";
  x: number;
  y: number;
  w: number;
  h: number;
  text?: string;
  sub?: string; // second line, smaller (e.g. jurisdiction, detail)
  dashed?: boolean; // dashed border — disregarded / foreign / branch entities
  rotation?: number; // degrees; used to aim flow-arrow triangles
  arrowDir?: "up" | "down" | "left" | "right"; // flow-arrowhead direction (no rotation needed)
  style: PrimStyle;
}

export interface LinePrim {
  kind: "line";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  label?: string; // e.g. ownership percentage on an ownership edge, or a flow label
  dashed?: boolean; // dashed line — flow arrows / non-standard ownership
  style: PrimStyle;
}

export interface TextPrim {
  kind: "text";
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  style: PrimStyle;
}

export type Primitive = BoxPrim | LinePrim | TextPrim;

export interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Palette {
  primary: string; // dominant fill (e.g. "1E2761")
  secondary: string; // supporting fill
  accent: string;
  ink: string; // dark text
  paper: string; // light text on dark fills
}

export const DEFAULT_PALETTE: Palette = {
  primary: "1E2761",
  secondary: "CADCFC",
  accent: "028090",
  ink: "1F2937",
  paper: "FFFFFF",
};

export const DEFAULT_REGION: Region = { x: 60, y: 100, w: 840, h: 400 };
